import { CommonModule, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEnCa from '@angular/common/locales/en-CA';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeFrCa from '@angular/common/locales/fr-CA';
import {
    FactoryProvider,
    forwardRef,
    Inject,
    Injectable,
    LOCALE_ID,
    ModuleWithProviders,
    NgModule,
    Provider,
} from '@angular/core';
import { CmpsIconModule, CmpsPortalModule } from '@cmps/ng-uikit';
import {
    TranslateLoader,
    TranslateModule,
    TranslateModuleConfig,
    TranslateService,
} from '@ngx-translate/core';

import { CMPS_I18N_MAP, DEFAULT_LOCALE, LOCALES } from './i18n.model';
import { I18nService } from './i18n.service';
import { I18nLoaderService } from './loader.i18n.service';
import { SwitcherI18nComponent } from './switcher.i18n.component';

export function registerLocales(): void {
    [localeEn, localeEnCa, localeFr, localeFrCa, localeDe, localeEs].forEach(
        (locale) => registerLocaleData(locale),
    );
}

// TODO move?
export function I18nLocaleFactory(service: I18nService): string {
    const locale = service.currentLocale;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    return LOCALES.indexOf(service.currentLocale) !== -1
        ? locale
        : DEFAULT_LOCALE;
}

export const I18nLocaleProvider: FactoryProvider = {
    provide: LOCALE_ID,
    useFactory: I18nLocaleFactory,
    deps: [I18nService],
};

const TRANSLATE_MODULE_CONFIG: TranslateModuleConfig = {
    loader: {
        provide: TranslateLoader,
        useExisting: forwardRef(() => I18nLoaderService),
    },
};

function registerMaps(service: I18nLoaderService, maps: string[]): void {
    maps.forEach((value) =>
        service.registerTranslationIdMap(JSON.parse(value)),
    );
}

export function provideMap(map: string): Provider[] {
    return [{ provide: CMPS_I18N_MAP, useValue: map, multi: true }];
}

@Injectable({
    providedIn: 'root',
})
export class LazyTranslateService extends TranslateService {}

@NgModule({
    imports: [
        CommonModule,
        TranslateModule.forChild(TRANSLATE_MODULE_CONFIG),
        CmpsIconModule,
        CmpsPortalModule,
    ],
    declarations: [SwitcherI18nComponent],
    exports: [TranslateModule, SwitcherI18nComponent],
})
export class CmpsLazyI18nModule {
    constructor(
        private readonly _service: I18nLoaderService,
        @Inject(CMPS_I18N_MAP) private readonly _maps: string[],
    ) {
        registerMaps(_service, _maps);
    }

    static forChild(map: string): ModuleWithProviders<CmpsLazyI18nModule> {
        return {
            ngModule: CmpsLazyI18nModule,
            providers: [
                provideMap(map),
                {
                    provide: TranslateService,
                    useExisting: forwardRef(() => LazyTranslateService),
                },
            ],
        };
    }
}

@NgModule({
    imports: [TranslateModule.forRoot(TRANSLATE_MODULE_CONFIG)],
    exports: [TranslateModule],
})
export class CmpsI18nModule {
    constructor(
        private readonly _service: I18nLoaderService,
        @Inject(CMPS_I18N_MAP) private readonly _maps: string[],
    ) {
        registerMaps(_service, _maps);
    }

    static forRoot(map: string): ModuleWithProviders<CmpsI18nModule> {
        return {
            ngModule: CmpsI18nModule,
            providers: [
                provideMap(map),
                I18nService,
                I18nLoaderService,
                LazyTranslateService,
                {
                    provide: TranslateService,
                    useExisting: forwardRef(() => LazyTranslateService),
                },
            ],
        };
    }
}
